import type { Cart, Conn, Fence, Me, Store } from './types';

/* API response shapes (subset of fields Companion reads) */

export interface ApiMe {
  email: string;
  first_name: string;
  last_name: string;
}

export interface ApiLocation {
  id: string;
  store_number: string;
  cart_count: number;
  region: string | null;
}

export interface ApiRegion {
  id: string;
  name: string;
}

export interface ApiCartStatus {
  id: string;
  external_id: string;
  name: string | null;
  serial_number: string | null;
  latest_telemetry: {
    timestamp: string;
    batt_soc: number | null;
    charger_state: number | null;
  } | null;
}

/**
 * A proper `is_charging` boolean is coming server-side; keep the
 * charger_state interpretation in this one function so the swap is one line.
 */
function isPlugged(telemetry: NonNullable<ApiCartStatus['latest_telemetry']>): boolean {
  return telemetry.charger_state === 1;
}

/**
 * Minutes since the telemetry timestamp, rounded down. A timestamp in the
 * future is invalid → null (known device bug ships year-2036 timestamps until
 * a firmware fix lands). Up to 60s of clock skew clamps to 0.
 */
export function minsSince(timestamp: string, now: number = Date.now()): number | null {
  const ts = Date.parse(timestamp);
  if (Number.isNaN(ts)) return null;
  const diffMs = now - ts;
  if (diffMs < -60_000) return null;
  return Math.max(0, Math.floor(diffMs / 60_000));
}

/**
 * Silent for 48 h or more → off, matching the portal's Cart Health Heartbeat
 * check (API HEARTBEAT_SILENT_AFTER). Unknown → off.
 */
export const SILENT_AFTER_MINS = 48 * 60;

/** From 24 h silent the "Last heard" line shows, before the cart grays at 48 h. */
export const LAST_HEARD_AFTER_MINS = 24 * 60;

export function connFromMins(mins: number | null): Conn {
  if (mins === null) return 'off';
  return mins < SILENT_AFTER_MINS ? 'ok' : 'off';
}

/** GET /locations/:id/cart-health/ — the subset Companion reads. */
export interface ApiLocationHealth {
  carts: Record<string, { alerting: string[] }>;
}

/**
 * In/out comes from the API's Cart Health GPS check, never from raw GPS here:
 * docked carts read outside the fence ~23% of the time, and the server check
 * allows for that drift (alerts only past 350 m beyond the outer fence, judged
 * on the last real fix). A cart the check isn't alerting on reads 'in'.
 */
export function fenceFromHealth(alerting: string[] | undefined): Fence {
  return alerting?.includes('GPS') ? 'out' : 'in';
}

/**
 * The error mark follows Cart Health's Hardware check, as the portal does
 * (API ADR-0002), never the raw telemetry `errors` code: a firmware diagnostic
 * such as a persistent `Storage` would otherwise flag a cart the dashboard
 * shows as healthy.
 */
export function errFromHealth(alerting: string[] | undefined): boolean {
  return alerting?.includes('Hardware') ?? false;
}

export function cartFromApi(
  api: ApiCartStatus,
  now: number = Date.now(),
  alerting?: string[]
): Cart {
  const t = api.latest_telemetry;
  const mins = t ? minsSince(t.timestamp, now) : null;
  return {
    name: api.name || api.external_id,
    serial: api.serial_number || api.external_id,
    pct: t?.batt_soc != null ? Math.round(t.batt_soc) : 0,
    plugged: t ? isPlugged(t) : false,
    fence: fenceFromHealth(alerting),
    err: errFromHealth(alerting),
    conn: connFromMins(mins),
    minsSinceHeard: mins,
  };
}

export function storeFromApi(
  location: ApiLocation,
  regionNames: Map<string, string>
): Store {
  return {
    id: location.id,
    name: location.store_number,
    region: (location.region && regionNames.get(location.region)) || 'Unassigned',
    cartCount: location.cart_count,
  };
}

export function meFromApi(api: ApiMe): Me {
  const name = [api.first_name, api.last_name].filter(Boolean).join(' ') || api.email;
  return {
    name,
    email: api.email,
  };
}
